import { useState } from "react";
import { ArrowLeft, LockKeyhole, UserRoundPlus } from "lucide-react";
import { Link } from "react-router-dom";
import logoFull from "@/assets/tagforce-logo.png";
import { api, formatApiError } from "@/lib/api";

const emptyForm = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  phone: "",
  website: "",
};

export default function ProviderRegistrationPage() {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function register(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");
    try {
      await api.post("/providers/register", form);
      setForm(emptyForm);
      setNotice(
        "Registration received. TAG will complete your profile and approve access before sign-in.",
      );
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="provider-registration-page" data-testid="provider-registration-page">
      <div className="provider-registration-backdrop" aria-hidden="true" />
      <section className="provider-registration-shell">
        <header className="provider-registration-header">
          <Link
            className="provider-back-link"
            data-testid="provider-registration-login-link"
            to="/login"
          >
            <ArrowLeft size={16} /> Back to sign in
          </Link>
          <div className="saas-brand-lockup">
            <img alt="TAGForce" className="saas-brand-logo" src={logoFull} />
            <span className="trademark">™</span>
          </div>
        </header>
        <div className="provider-registration-heading">
          <p className="saas-eyebrow">PROVIDER ACCESS</p>
          <h1>Join the TAGForce provider network</h1>
          <p>
            Share the basics now. TAG will securely complete your professional profile after review.
          </p>
        </div>
        {error && (
          <p className="form-message error-message" data-testid="provider-registration-error">
            {error}
          </p>
        )}
        {notice && (
          <p className="form-message success-message" data-testid="provider-registration-success">
            {notice}
          </p>
        )}
        {!notice && (
          <form
            className="provider-registration-form"
            data-testid="provider-registration-form"
            onSubmit={register}
          >
            <label>
              First name
              <input
                data-testid="provider-first-name-input"
                value={form.first_name}
                onChange={(event) => setForm({ ...form, first_name: event.target.value })}
                required
              />
            </label>
            <label>
              Last name
              <input
                data-testid="provider-last-name-input"
                value={form.last_name}
                onChange={(event) => setForm({ ...form, last_name: event.target.value })}
                required
              />
            </label>
            <label className="provider-form-wide">
              Email
              <input
                data-testid="provider-email-input"
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                required
              />
            </label>
            <label>
              Set password
              <input
                data-testid="provider-password-input"
                type="password"
                minLength="8"
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
                required
              />
            </label>
            <label>
              Phone
              <input
                data-testid="provider-phone-input"
                type="tel"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
                required
              />
            </label>
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="provider-website">Website</label>
              <input
                autoComplete="off"
                data-testid="provider-website-honeypot"
                id="provider-website"
                name="website"
                tabIndex={-1}
                type="text"
                value={form.website}
                onChange={(event) => setForm({ ...form, website: event.target.value })}
              />
            </div>
            <button
              className="saas-auth-submit provider-form-wide"
              data-testid="provider-registration-submit-button"
              disabled={isSubmitting}
            >
              <UserRoundPlus size={17} />
              <span>{isSubmitting ? "Submitting…" : "Submit for approval"}</span>
            </button>
          </form>
        )}
        <footer className="provider-registration-footer">
          <LockKeyhole size={15} /> Professional records are completed privately by authorized TAG
          administrators.
        </footer>
      </section>
    </main>
  );
}
